<template>
  <div class="rr-page">
    <div class="rr-page-header">
      <h1 class="rr-page-title">Остаток ТМЦ на ЖЦП</h1>
    </div>

    <div class="rr-filters-card">
      <div class="rr-filters-card__body">
        <div class="rr-filters-toolbar">
          <div class="rr-filters-main">
            <CustomSelect
              class="rr-filter-field"
              :options="residents"
              label="ЖК"
              option-value-key="resident_id"
              option-label-key="resident_name"
              :value="filters.resident_id"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!filters.resident_id"
              @change="onResident"
            />
            <CustomSelect
              class="rr-filter-field"
              :options="projectTypes"
              label="Тип проекта"
              option-value-key="project_type_id"
              option-label-key="project_type_name"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!filters.project_type_id"
              :value="filters.project_type_id"
              :disabled="!filters.resident_id"
              @change="onProjectType"
            />
            <CustomSelect
              class="rr-filter-field"
              :options="entrances"
              label="Подъезд"
              option-value-key="entrance"
              option-label-key="entrance_name"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!filters.entrance"
              :value="filters.entrance"
              :disabled="!filters.resident_id"
              @change="onEntrance"
            />
            <CustomSelect
              class="rr-filter-field"
              :options="floors"
              label="Этаж"
              option-value-key="level_num"
              option-label-key="floor_name"
              clearable
              fullWidth
              placeholder="Выберите.."
              :useInput="!filters.level_num"
              :value="filters.level_num"
              :disabled="!filters.resident_id"
              @change="onFloor"
            />
          </div>

          <div class="rr-filters-actions">
            <q-btn
              class="rr-filter-action-btn"
              outline
              color="grey-7"
              no-caps
              dense
              icon="filter_alt_off"
              label="Сбросить"
              :disable="!hasActiveFilters"
              @click="resetFilters"
            />
            <q-btn
              class="rr-filter-action-btn"
              color="positive"
              unelevated
              no-caps
              dense
              icon="download"
              label="Excel"
              :disable="!filters.resident_id || exporting"
              :loading="exporting"
              @click="exportExcel"
            />
          </div>
        </div>
      </div>
    </div>

    <div v-if="rows.length" class="rr-totals-bar q-mt-sm">
      <div class="rr-total-block">
        <div class="rr-total-label">Итого план, тг.</div>
        <div class="rr-total-value">
          <Amount :value="totalPlanSum" round="2" />
        </div>
      </div>
      <div class="rr-totals-sep" />
      <div class="rr-total-block">
        <div class="rr-total-label">Итого перерасход, тг.</div>
        <div class="rr-total-value">
          <Amount :value="totalOverspendSum" round="2" />
        </div>
      </div>
      <div class="rr-totals-sep" />
      <div class="rr-total-block">
        <div class="rr-total-label">Итого БУ, тг.</div>
        <div class="rr-total-value">
          <Amount :value="totalBuSum" round="2" />
        </div>
      </div>
      <div class="rr-totals-sep" />
      <div class="rr-total-block">
        <div class="rr-total-label">Итого списание, тг.</div>
        <div class="rr-total-value">
          <Amount :value="totalSpisanieSum" round="2" />
        </div>
      </div>
      <div class="rr-totals-sep" />
      <div class="rr-total-block">
        <div class="rr-total-label">Итого остаток, тг.</div>
        <div class="rr-total-value">
          <Amount :value="totalOstatokSum" round="2" />
        </div>
      </div>
    </div>

    <q-banner v-if="errorText" rounded class="rr-banner q-mt-md">
      <div class="rr-banner__text">{{ errorText }}</div>
    </q-banner>

    <q-linear-progress
      v-if="loading"
      indeterminate
      color="primary"
      track-color="grey-3"
      class="rr-progress q-mt-md"
      rounded
      size="4px"
    />

    <q-card v-if="rows.length" flat bordered class="rr-card rr-card--table q-mt-md">
      <div ref="tableScroll" class="rr-table-scroll" @scroll="onTableScroll">
        <table ref="tableEl" class="rr-table" cellspacing="0">
          <colgroup>
            <col class="rr-col-code" />
            <col class="rr-col-name" />
            <col class="rr-col-unit" />
            <col class="rr-col-num" />
            <col class="rr-col-num" />
            <col class="rr-col-num" />
            <col class="rr-col-num" />
            <col class="rr-col-num" />
            <col class="rr-col-num" />
            <col class="rr-col-num" />
            <col class="rr-col-num" />
            <col class="rr-col-num" />
            <col class="rr-col-num" />
            <col class="rr-col-num" />
            <col class="rr-col-num" />
            <col class="rr-col-num" />
            <col class="rr-col-num" />
            <col class="rr-col-num" />
          </colgroup>
          <thead class="rr-thead">
            <tr class="rr-tr rr-tr--head-top">
              <th class="rr-th rr-th--sticky-left rr-th--divider-right rr-th--code-head" rowspan="3">Код ресурса</th>
              <th class="rr-th rr-th--name rr-th--divider-right" rowspan="3">Наименование</th>
              <th class="rr-th rr-th--divider-right" rowspan="3">Ед.</th>
              <th class="rr-th rr-th--root rr-th--sbs rr-th--divider-right" colspan="6">SBS</th>
              <th class="rr-th rr-th--root rr-th--bu" colspan="9">БУ</th>
            </tr>
            <tr class="rr-tr rr-tr--head-mid">
              <th class="rr-th rr-th--group rr-th--plan rr-th--divider-right" colspan="3">План</th>
              <th class="rr-th rr-th--group rr-th--overspend rr-th--divider-right" colspan="3">Перерасход</th>
              <th class="rr-th rr-th--group rr-th--bu rr-th--divider-right" colspan="3">Приход</th>
              <th class="rr-th rr-th--group rr-th--spisanie rr-th--divider-right" colspan="3">Списание</th>
              <th class="rr-th rr-th--group rr-th--bu" colspan="3">Остаток</th>
            </tr>
            <tr class="rr-tr rr-tr--head-sub">
              <th class="rr-th rr-th--num rr-th--plan">Кол</th>
              <th class="rr-th rr-th--num rr-th--plan">Цена</th>
              <th class="rr-th rr-th--num rr-th--plan rr-th--divider-right">Сумма</th>
              <th class="rr-th rr-th--num rr-th--overspend">Кол</th>
              <th class="rr-th rr-th--num rr-th--overspend">Цена</th>
              <th class="rr-th rr-th--num rr-th--overspend rr-th--divider-right">Сумма</th>
              <th class="rr-th rr-th--num rr-th--bu">Кол</th>
              <th class="rr-th rr-th--num rr-th--bu">Цена</th>
              <th class="rr-th rr-th--num rr-th--bu rr-th--divider-right">Сумма</th>
              <th class="rr-th rr-th--num rr-th--spisanie">Кол</th>
              <th class="rr-th rr-th--num rr-th--spisanie">Цена</th>
              <th class="rr-th rr-th--num rr-th--spisanie rr-th--divider-right">Сумма</th>
              <th class="rr-th rr-th--num rr-th--bu">Кол</th>
              <th class="rr-th rr-th--num rr-th--bu">Цена</th>
              <th class="rr-th rr-th--num rr-th--bu">Сумма</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, idx) in rows"
              :key="idx"
              class="rr-tr rr-tr--data"
            >
              <td class="rr-td rr-td--sticky-left rr-td--code rr-td--divider-right">{{ row.kod_resursa || '—' }}</td>
              <td class="rr-td rr-td--name rr-td--divider-right">{{ row.material_name || '—' }}</td>
              <td class="rr-td rr-td--divider-right">{{ row.unit_name || '—' }}</td>

              <td class="rr-td rr-td--num">
                <Amount v-if="row.plan_amount != null" :value="row.plan_amount" />
                <span v-else class="rr-dash">—</span>
              </td>
              <td class="rr-td rr-td--num">
                <Amount v-if="row.plan_amount != null && row.plan_price != null" :value="row.plan_price" round="2" />
                <span v-else class="rr-dash">—</span>
              </td>
              <td class="rr-td rr-td--num rr-td--sum rr-td--divider-right">
                <Amount
                  v-if="row.plan_amount != null && row.plan_price != null"
                  :value="calcSum(row.plan_amount, row.plan_price)"
                  round="2"
                />
                <span v-else class="rr-dash">—</span>
              </td>

              <td class="rr-td rr-td--num">
                <Amount v-if="isPositive(row.overspend_amount)" :value="row.overspend_amount" />
                <span v-else class="rr-dash">—</span>
              </td>
              <td class="rr-td rr-td--num">
                <Amount v-if="isPositive(row.overspend_amount) && row.overspend_price != null" :value="row.overspend_price" round="2" />
                <span v-else class="rr-dash">—</span>
              </td>
              <td class="rr-td rr-td--num rr-td--sum rr-td--divider-right">
                <Amount
                  v-if="isPositive(row.overspend_amount) && row.overspend_price != null"
                  :value="calcSum(row.overspend_amount, row.overspend_price)"
                  round="2"
                />
                <span v-else class="rr-dash">—</span>
              </td>

              <td class="rr-td rr-td--num">
                <Amount v-if="row.bu_amount != null" :value="row.bu_amount" />
                <span v-else class="rr-dash">—</span>
              </td>
              <td class="rr-td rr-td--num">
                <Amount v-if="row.bu_amount != null && row.bu_price != null" :value="row.bu_price" round="2" />
                <span v-else class="rr-dash">—</span>
              </td>
              <td class="rr-td rr-td--num rr-td--sum rr-td--divider-right">
                <Amount
                  v-if="row.bu_amount != null && row.bu_price != null"
                  :value="calcSum(row.bu_amount, row.bu_price)"
                  round="2"
                />
                <span v-else class="rr-dash">—</span>
              </td>

              <td class="rr-td rr-td--num">
                <Amount v-if="row.spisanie_qty != null" :value="row.spisanie_qty" />
                <span v-else class="rr-dash">—</span>
              </td>
              <td class="rr-td rr-td--num">
                <Amount v-if="row.spisanie_qty != null && row.spisanie_price != null" :value="row.spisanie_price" round="2" />
                <span v-else class="rr-dash">—</span>
              </td>
              <td class="rr-td rr-td--num rr-td--sum rr-td--divider-right">
                <Amount
                  v-if="row.spisanie_qty != null && row.spisanie_amount != null"
                  :value="row.spisanie_amount"
                  round="2"
                />
                <span v-else class="rr-dash">—</span>
              </td>

              <td class="rr-td rr-td--num">
                <Amount v-if="row.ostatok_qty != null" :value="row.ostatok_qty" />
                <span v-else class="rr-dash">—</span>
              </td>
              <td class="rr-td rr-td--num">
                <Amount v-if="row.ostatok_qty != null && row.ostatok_price != null" :value="row.ostatok_price" round="2" />
                <span v-else class="rr-dash">—</span>
              </td>
              <td class="rr-td rr-td--num rr-td--sum">
                <Amount
                  v-if="row.ostatok_qty != null && row.ostatok_amount != null"
                  :value="row.ostatok_amount"
                  round="2"
                />
                <span v-else class="rr-dash">—</span>
              </td>
            </tr>
          </tbody>
        </table>
        <div ref="fakeScrollbar" class="rr-fake-scrollbar" @scroll="onFakeScroll">
          <div ref="fakeScrollbarInner" class="rr-fake-scrollbar__inner"></div>
        </div>
      </div>
    </q-card>

    <q-card
      v-else-if="!loading && searched"
      flat
      bordered
      class="rr-empty q-mt-md"
    >
      <q-card-section class="column items-center q-py-xl q-px-md text-center">
        <div class="rr-empty__title">Нет данных</div>
        <div class="rr-empty__text">Смените фильтры или проверьте данные.</div>
      </q-card-section>
    </q-card>
  </div>
</template>

<script>
import Amount from '@/components/common/Amount.vue';
import CustomSelect from '@/components/form/CustomSelect';
import {
  exportResourceRemainderReport,
  getResourceRemainderReport,
  getResourceStatementProjectTypes,
  getResourceStatementResidents,
  getResourceStatementEntrances,
  getResourceStatementFloorsByResident,
} from '@/views/ResidentialSettings/services';
import { getPackagePurchaseTypes } from '@/views/PackageEntry/services';

function num(v) {
  if (v == null || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export default {
  name: 'ResourceRemainderReport',
  components: { CustomSelect, Amount },
  data() {
    return {
      residents: [],
      projectTypes: [],
      entrances: [],
      floors: [],
      purchaseTypes: [],
      filters: {
        resident_id: '',
        project_type_id: '',
        entrance: '',
        level_num: '',
        purchase_type_id: '',
      },
      rows: [],
      loading: false,
      exporting: false,
      searched: false,
      errorText: '',
      syncingScroll: false,
    };
  },
  computed: {
    totalPlanSum() {
      return this.rows.reduce((acc, r) => acc + num(r.plan_amount) * num(r.plan_price), 0);
    },
    totalOverspendSum() {
      return this.rows.reduce((acc, r) => {
        const amount = num(r.overspend_amount);
        if (amount <= 0) return acc;
        return acc + amount * num(r.overspend_price);
      }, 0);
    },
    totalBuSum() {
      return this.rows.reduce((acc, r) => acc + num(r.bu_amount) * num(r.bu_price), 0);
    },
    totalSpisanieSum() {
      return this.rows.reduce((acc, r) => acc + num(r.spisanie_amount), 0);
    },
    totalOstatokSum() {
      return this.rows.reduce((acc, r) => acc + num(r.ostatok_amount), 0);
    },
    hasActiveFilters() {
      const f = this.filters;
      return !!(f.resident_id || f.project_type_id || f.entrance || f.level_num || f.purchase_type_id);
    },
  },
  async mounted() {
    this.residents = (await getResourceStatementResidents()) || [];
    this.purchaseTypes = (await getPackagePurchaseTypes()) || [];
    this._resizeObserver = null;
  },
  beforeUnmount() {
    if (this._resizeObserver) {
      this._resizeObserver.disconnect();
      this._resizeObserver = null;
    }
  },
  watch: {
    rows() {
      this.$nextTick(() => this.syncFakeScrollbarWidth());
    },
  },
  methods: {
    calcSum(amount, price) {
      return num(amount) * num(price);
    },
    isPositive(value) {
      if (value == null) return false;
      return Number(value) > 0;
    },
    syncFakeScrollbarWidth() {
      const tableEl = this.$refs.tableEl;
      const fakeEl = this.$refs.fakeScrollbar;
      const fakeInner = this.$refs.fakeScrollbarInner;
      const scrollEl = this.$refs.tableScroll;
      if (!tableEl || !fakeEl || !fakeInner || !scrollEl) return;
      fakeEl.style.width = scrollEl.clientWidth + 'px';
      fakeInner.style.width = tableEl.scrollWidth + 'px';
      if (this._resizeObserver) this._resizeObserver.disconnect();
      this._resizeObserver = new ResizeObserver(() => {
        fakeEl.style.width = scrollEl.clientWidth + 'px';
        fakeInner.style.width = tableEl.scrollWidth + 'px';
      });
      this._resizeObserver.observe(scrollEl);
    },
    onTableScroll() {
      if (this.syncingScroll) return;
      const scrollEl = this.$refs.tableScroll;
      const fakeEl = this.$refs.fakeScrollbar;
      if (!scrollEl || !fakeEl) return;
      this.syncingScroll = true;
      fakeEl.scrollLeft = scrollEl.scrollLeft;
      this.syncingScroll = false;
    },
    onFakeScroll() {
      if (this.syncingScroll) return;
      const scrollEl = this.$refs.tableScroll;
      const fakeEl = this.$refs.fakeScrollbar;
      if (!scrollEl || !fakeEl) return;
      this.syncingScroll = true;
      scrollEl.scrollLeft = fakeEl.scrollLeft;
      this.syncingScroll = false;
    },
    resetFilters() {
      this.filters.resident_id = '';
      this.filters.project_type_id = '';
      this.filters.entrance = '';
      this.filters.level_num = '';
      this.filters.purchase_type_id = '';
      this.projectTypes = [];
      this.entrances = [];
      this.floors = [];
      this.clearReport();
    },
    clearReport() {
      this.rows = [];
      this.searched = false;
      this.errorText = '';
    },
    async exportExcel() {
      if (!this.filters.resident_id) return;
      this.exporting = true;
      try {
        const params = { resident_id: this.filters.resident_id };
        if (this.filters.project_type_id) params.project_type_id = this.filters.project_type_id;
        if (this.filters.entrance) params.entrance = this.filters.entrance;
        if (this.filters.level_num) params.level_num = this.filters.level_num;
        if (this.filters.purchase_type_id) params.purchase_type_id = this.filters.purchase_type_id;

        const resident = this.residents.find(
          (r) => String(r.resident_id) === String(this.filters.resident_id),
        );
        const residentName = resident?.resident_name
          ? resident.resident_name.replace(/[\\/:*?"<>|]/g, '_')
          : 'resident';

        const blob = await exportResourceRemainderReport(params);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `resource_remainder_${residentName}.xlsx`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        this.errorText = e?.response?.data?.message || e?.message || 'Ошибка экспорта';
      } finally {
        this.exporting = false;
      }
    },
    async onPurchaseType(v) {
      this.filters.purchase_type_id = v || '';
      if (this.filters.resident_id) await this.fetchReport();
    },
    async onResident(v) {
      this.filters.resident_id = v;
      this.filters.project_type_id = '';
      this.filters.entrance = '';
      this.filters.level_num = '';
      this.filters.purchase_type_id = '';
      this.projectTypes = [];
      this.entrances = [];
      this.floors = [];
      if (!v) {
        this.clearReport();
        return;
      }
      const [types, entrances, floors] = await Promise.all([
        getResourceStatementProjectTypes(v),
        getResourceStatementEntrances(v),
        getResourceStatementFloorsByResident(v),
      ]);
      this.projectTypes = types || [];
      this.entrances = entrances || [];
      this.floors = floors || [];
      await this.fetchReport();
    },
    async onProjectType(v) {
      this.filters.project_type_id = v || '';
      if (this.filters.resident_id) await this.fetchReport();
    },
    async onEntrance(v) {
      this.filters.entrance = v || '';
      if (this.filters.resident_id) await this.fetchReport();
    },
    async onFloor(v) {
      this.filters.level_num = v || '';
      if (this.filters.resident_id) await this.fetchReport();
    },
    async fetchReport() {
      if (!this.filters.resident_id) {
        this.clearReport();
        return;
      }
      this.errorText = '';
      this.loading = true;
      this.searched = true;
      try {
        const params = { resident_id: this.filters.resident_id };
        if (this.filters.project_type_id) params.project_type_id = this.filters.project_type_id;
        if (this.filters.entrance) params.entrance = this.filters.entrance;
        if (this.filters.level_num) params.level_num = this.filters.level_num;
        if (this.filters.purchase_type_id) params.purchase_type_id = this.filters.purchase_type_id;

        const res = await getResourceRemainderReport(params);
        this.rows = res?.data || [];
      } catch (e) {
        this.errorText = e?.response?.data?.message || e?.message || 'Ошибка загрузки';
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
@use "@/assets/styles/quasar.variables.scss" as *;

.rr-page {
  padding: 8px 12px 48px;
  max-width: 100%;
  min-height: calc(100vh - 56px);
  min-height: calc(100dvh - 56px);
  box-sizing: border-box;
}

.rr-page-header {
  margin-bottom: 8px;
}

.rr-page-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.3;
  color: #0f172a;
}

.rr-filters-card {
  margin-bottom: 8px;
  padding: 8px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: linear-gradient(180deg, #fafbfc 0%, #fff 100%);
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.rr-filters-card__body {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.rr-filters-toolbar {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
  gap: 12px;
}

.rr-filters-main {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
}

.rr-filters-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.rr-filter-field {
  min-width: 0;
  width: 100%;

  :deep(.custom-select__wrapper) {
    width: 100%;
  }

  :deep(.q-field) {
    width: 100%;
  }

  :deep(.q-field__control) {
    min-height: 40px;
  }

  :deep(.q-field__native) {
    min-height: 24px;
  }
}

.rr-filter-action-btn {
  font-size: 12px;
  min-height: 36px;
  width: 118px;
  min-width: 118px;
  padding: 0 8px;
}

@media (max-width: 1280px) {
  .rr-filters-toolbar {
    grid-template-columns: 1fr;
  }

  .rr-filters-main {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .rr-filters-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 720px) {
  .rr-filters-main {
    grid-template-columns: 1fr;
  }

  .rr-filters-actions {
    width: 100%;
  }

  .rr-filter-action-btn {
    flex: 1 1 calc(50% - 4px);
    min-width: 0;
  }
}

.rr-totals-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0;
  background: #fff;
  border: 1px solid $gray-light;
  border-radius: 8px;
  padding: 10px 16px;
}

.rr-totals-sep {
  width: 1px;
  height: 32px;
  background: $gray-light;
  margin: 0 16px;
  flex-shrink: 0;
}

.rr-total-block {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.rr-total-label {
  font-size: 10px;
  color: $gray;
  font-weight: 500;
  margin-bottom: 1px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.rr-total-value {
  font-size: 15px;
  font-weight: 700;
  color: $title;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}

.rr-banner {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #78350f;
}

.rr-banner__text {
  font-size: 14px;
  line-height: 1.45;
}

.rr-progress {
  max-width: 100%;
}

.rr-card--table {
  padding-bottom: 0;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}

.rr-table-scroll {
  overflow-x: hidden;
  overflow-y: auto;
  max-height: calc(100dvh - 120px);
  max-height: calc(100vh - 120px);
  min-height: min(58dvh, 520px);
  min-height: min(58vh, 520px);
  -webkit-overflow-scrolling: touch;
  border-radius: 0 0 12px 12px;
  box-shadow: inset 0 1px 0 rgba(15, 23, 42, 0.06);
}

.rr-fake-scrollbar {
  position: sticky;
  bottom: 0;
  left: 0;
  overflow-x: auto;
  overflow-y: hidden;
  height: 14px;
  min-height: 14px;
  background: #f1f5f9;
  border-top: 1px solid #e2e8f0;
  z-index: 10;
}

.rr-fake-scrollbar__inner {
  height: 1px;
}

.rr-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;
}

.rr-col-code { width: 130px; }
.rr-col-name { width: 170px; }
.rr-col-unit { width: 40px; }
.rr-col-num  { width: 100px; }

.rr-thead {
  position: sticky;
  top: 0;
  z-index: 8;
  box-shadow: 0 2px 0 rgba(15, 23, 42, 0.08);
}

.rr-th,
.rr-td {
  border-bottom: 1px solid #e8ecf1;
  padding: 5px 4px;
  vertical-align: middle;
}

.rr-th {
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  font-size: 10px;
  background: #1e293b;
  color: #f8fafc;
}

.rr-th--code-head {
  white-space: normal;
  line-height: 1.25;
  vertical-align: middle;
}

.rr-th--root {
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-bottom: 1px solid rgba(248, 250, 252, 0.25);
}

.rr-th--group {
  font-size: 10px;
  letter-spacing: 0.02em;
  border-bottom: 1px solid rgba(248, 250, 252, 0.2);
}

.rr-th--sbs {
  background: #1e3a5f;
}

.rr-th--plan {
  background: #1e3a5f;
}

.rr-th--limit {
  background: #1e3a5f;
}

.rr-th--overspend {
  background: #1e3a5f;
}

.rr-th--bu {
  background: #4a2d5e;
}

.rr-th--spisanie {
  background: #4a2d5e;
}

.rr-th--divider-right {
  border-right: 2px solid #0f172a;
}

.rr-td--divider-right {
  border-right: 2px solid #c7d2e0;
}

.rr-th--sticky-left {
  position: sticky;
  left: 0;
  z-index: 9;
  text-align: left;
  box-shadow: 4px 0 12px -6px rgba(15, 23, 42, 0.18);
}

.rr-th--name {
  text-align: left;
}

.rr-th--num {
  text-align: right;
}

.rr-td--sticky-left {
  position: sticky;
  left: 0;
  z-index: 3;
  background: #fff;
  box-shadow: 4px 0 12px -6px rgba(15, 23, 42, 0.12);
}

.rr-tr--data:nth-child(even) .rr-td--sticky-left {
  background: #fafbfc;
}

.rr-tr--data:hover .rr-td {
  background: #f0f7ff !important;
}

.rr-td--code {
  font-size: 10px;
  font-family: monospace;
  color: $gray-dark;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rr-td--name {
  font-weight: 500;
  font-size: 11px;
  line-height: 1.35;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

.rr-td--num {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
  font-size: 11px;
  padding: 5px 4px !important;
}

.rr-td--sum {
  font-weight: 600;
}

.rr-tr--data:nth-child(even) .rr-td {
  background: #fafbfc;
}

.rr-dash {
  color: $gray;
  opacity: 0.75;
}

.rr-empty {
  border-radius: 8px;
  background: #fafbfc;
  border: 1px solid $gray-light;
}

.rr-empty__title {
  font-size: 15px;
  font-weight: 600;
  color: $title;
  margin-bottom: 6px;
}

.rr-empty__text {
  font-size: 13px;
  line-height: 1.5;
  color: $gray;
  max-width: 360px;
}
</style>
